import { elements, feedback } from '@ecosystem/newron-design-system';
import { WithHiddenListBox } from '@components/design';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { ComponentName, SaleType } from '@models/Products';
import { DisplayOptions, DisplayTypesOption } from './Types';
import {
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
  ChartInteractionTrackingFunctionParamSubset,
  useGenerateChartInteractionTrackingParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { capitalize } from 'lodash';
import { RI_PRODUCT_LEADERBOARD_SHOW_AND_RANK_BY } from '@pages/Utilities';
import { useProductAnalyzerGroupEventState } from '../product-table-grouping/utils/ProductAnalyzerGroupContext';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { SubSection } from '@utils/pageConfig/Types';

interface Props {
  selectedSaleType: SaleType;
  selectedDisplayType: DisplayOptions;
  onChangeDisplayType: Dispatch<SetStateAction<DisplayOptions>>;
  onChangeSelectedSaleType: Dispatch<SetStateAction<SaleType>>;
  setIsViewTypeChanged: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
}

const ProductsTableFilter: React.FC<Props> = ({
  selectedSaleType,
  selectedDisplayType,
  onChangeDisplayType,
  onChangeSelectedSaleType,
  setIsViewTypeChanged,
  isLoading,
}) => {
  //Define Chart Interaction Param initial values
  const chartInteractionParam = useGenerateChartInteractionTrackingParam('hitlistsRetailerClient');

  const { clearSelectedModelsGroup } = useProductAnalyzerGroupEventState();
  const { access } = usePageConfig();

  const switchDisplayType = (option: DisplayTypesOption | null) => {
    if (option) {
      chartInteractionTracking(option.value, chartInteractionParam, SubSection.PRODUCT_LEADERBOARD);
      onChangeDisplayType(option.value);
    }
  };
  const switchSelectedSaleType = (value: string) => {
    // runs setIsViewTypeChanged(false) to reset flag used to trigger chart type change hook
    setIsViewTypeChanged(false);
    chartInteractionTracking(value, chartInteractionParam, SubSection.PRODUCT_LEADERBOARD);
    onChangeSelectedSaleType(value as SaleType);
    clearSelectedModelsGroup();
  };
  const displayTypesOptions: DisplayTypesOption[] = [
    {
      value: DisplayOptions.data,
      label: DisplayOptions.data,
    },
    {
      value: DisplayOptions.feature,
      label: DisplayOptions.feature,
    },
  ];
  const defaultDisplayTypes = displayTypesOptions.find((d) => selectedDisplayType === d.label);
  const [updatedDisplayType, setUpdatedDisplayType] = useState<string>(
    selectedDisplayType ?? defaultDisplayTypes?.value,
  );

  // Chart Interaction Tracking
  const chartInteractionTracking = (
    val: string,
    chartInteractionTrackingFunctionParamSubset: ChartInteractionTrackingFunctionParamSubset,
    subSection?: SubSection,
  ) => {
    //Start Chart Interaction Tracking
    const customChartInteractionParam = {
      ...chartInteractionTrackingFunctionParamSubset,
      optionChosen: capitalize(val),
      chartName: ComponentName.PRODUCT_LEADERBOARD_TOP_SELLING_PRODUCTS,
      elementName: RI_PRODUCT_LEADERBOARD_SHOW_AND_RANK_BY,
      elementType: ChartElementType.FILTER,
      access: access,
      subSection,
    } as unknown as ChartInteractionTrackingFunctionParam;

    chartInteractionTrackingFunction(customChartInteractionParam);
    //End Chart Interaction Tracking
  };

  useEffect(() => {
    const selectedOption = displayTypesOptions.find((o) => o.value === updatedDisplayType);
    if (selectedOption) {
      switchDisplayType(selectedOption);
    }
  }, [updatedDisplayType]);

  const options = [
    { label: 'Revenue', value: SaleType.revenue },
    { label: 'Units', value: SaleType.volume },
  ];

  const getOptionLabel = (availableOptions: any[], value: string) => {
    const option = availableOptions.find((opt) => opt.value === value);
    return option ? option.label : null;
  };

  const memoizedSelectedShowAndRankByLabelOption = useMemo(
    () => getOptionLabel(options, selectedSaleType),
    [options, selectedSaleType],
  );
  const memoizedSelectedDisplayLabelOption = useMemo(
    () => getOptionLabel(displayTypesOptions, updatedDisplayType),
    [displayTypesOptions, updatedDisplayType],
  );

  return (
    <div className="flex-row flex-wrap items-center" data-test-id="leaderboard-table-filter">
      <WithHiddenListBox
        isDisabled={isLoading}
        label={RI_PRODUCT_LEADERBOARD_SHOW_AND_RANK_BY}
        options={options}
        value={memoizedSelectedShowAndRankByLabelOption}
        onChange={({ value }) => {
          switchSelectedSaleType(value);
        }}
      />
      <WithHiddenListBox
        isDisabled={isLoading}
        label={'display'}
        options={displayTypesOptions}
        value={memoizedSelectedDisplayLabelOption}
        onChange={({ value }) => {
          setUpdatedDisplayType(value);
        }}
      />
      <feedback.Tooltip placement="top" theme="light">
        <feedback.Tooltip.Content>
          <p>
            &apos;Show and rank by&apos; controls both the basis on which the Products are ranked against to determine
            the Market & Retailer ranking order, and the fact shown in the data table. &apos;Display&apos; selects
            between displaying Sales data and Feature information of the products.
          </p>
        </feedback.Tooltip.Content>
        <div>
          <elements.Icon color="brand">
            <elements.Icon.icons.HelpOutline />
          </elements.Icon>
        </div>
      </feedback.Tooltip>
    </div>
  );
};

export default ProductsTableFilter;
